import React, { useState, ReactNode } from "react";
import { HiEye, HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { useTranslation } from "react-i18next";

export interface ResponsivePreviewLayoutProps {
  children: ReactNode;

  previewContent: ReactNode;

  showPreviewButton?: boolean;

  showPreviewLabel?: string;

  hidePreviewLabel?: string;

  initialPreviewVisible?: boolean;

  configColSpan?: number;

  previewColSpan?: number;

  configClassName?: string;

  previewClassName?: string;

  buttonClassName?: string;

  onPreviewVisibilityChange?: (isVisible: boolean) => void;

  hasPreviewContent?: boolean;
}

export const ResponsivePreviewLayout: React.FC<
  ResponsivePreviewLayoutProps
> = ({
  children,
  previewContent,
  showPreviewButton = true,
  initialPreviewVisible = false,
  configColSpan = 5,
  previewColSpan = 7,
  configClassName = "",
  previewClassName = "",
  buttonClassName = "",
  onPreviewVisibilityChange,
  hasPreviewContent = true,
}) => {
  const { t } = useTranslation();
  const [isPreviewVisible, setIsPreviewVisible] = useState(
    initialPreviewVisible
  );

  const handleTogglePreview = () => {
    const newValue = !isPreviewVisible;
    setIsPreviewVisible(newValue);
    onPreviewVisibilityChange?.(newValue);
  };

  const showLabel = t("common.showPreview") as string;
  const hideLabel = t("common.hidePreview") as string;

  return (
    <div className="flex flex-col xl:grid xl:grid-cols-12 h-full relative">
      <div
        className={`xl:col-span-${configColSpan} col-span-12 h-full ${configClassName}`}
        style={{ gridColumn: `span ${configColSpan} / span ${configColSpan}` }}
      >
        {children}
      </div>

      <div
        className={`hidden xl:block xl:col-span-${previewColSpan} col-span-12 h-full ${previewClassName}`}
        style={{
          gridColumn: `span ${previewColSpan} / span ${previewColSpan}`,
        }}
      >
        {previewContent}
      </div>

      <div className="xl:hidden">
        <div
          className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300
            ${
              isPreviewVisible ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          onClick={handleTogglePreview}
          aria-hidden="true"
        />

        <div
          className={`fixed top-0 right-0 h-full w-[92vw] max-w-2xl bg-white shadow-lg z-50
            border-l-4 border-blue-600
            transform transition-transform duration-300 ease-in-out
            ${isPreviewVisible ? "translate-x-0" : "translate-x-full"}
            ${previewClassName}`}
        >
          <button
            onClick={handleTogglePreview}
            className="w-full flex items-center justify-between p-4 border-b border-gray-200 bg-blue-50 transition-colors cursor-pointer"
            aria-label={hideLabel}
          >
            <h3 className="text-lg font-semibold text-gray-800">{showLabel}</h3>
            <div className="p-2 rounded-full hover:bg-white/50 transition-colors">
              <HiChevronRight size={24} className="text-gray-600" />
            </div>
          </button>

          <div className="h-[calc(100%-65px)] overflow-y-auto">
            {previewContent}
          </div>
        </div>
      </div>

      {showPreviewButton && hasPreviewContent && (
        <button
          onClick={handleTogglePreview}
          className={`xl:hidden fixed top-1/4 right-0 z-30
            flex items-center gap-1 py-3 px-0.5
            bg-blue-600
            text-white font-semibold rounded-l-xl shadow-lg
            transition-all duration-300 hover:px-3
            ${isPreviewVisible ? "translate-x-full" : "translate-x-0"}
            ${buttonClassName}`}
          aria-label={showLabel}
        >
          <HiChevronLeft
            size={24}
            style={{ animation: "bounce-x 1.5s ease-in-out infinite" }}
          />
          <style>{`
            @keyframes bounce-x {
              0%, 100% { transform: translateX(0); }
              50% { transform: translateX(-6px); }
            }
          `}</style>
          <span className="text-base font-bold">{showLabel}</span>
          <HiEye size={18} className="mr-2 ml-1" />
        </button>
      )}
    </div>
  );
};

export default ResponsivePreviewLayout;
